import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AxiosError } from 'axios';
import { ClientsService } from 'src/app/services/clients/clients.service';
import { UsersService } from 'src/app/services/users/users.service';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { environment } from 'src/environments/environment';
import { HeaderAppComponent } from '../../headers/header-app/header-app.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { FormTemplateComponent } from '../../../shared/screen-templates/form-template/form-template.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { MatError } from '@angular/material/input';
import { PayeePhoneComponent } from '../../../shared/payee-form-fields/payee-phone/payee-phone.component';
import { PayeePasswordFormFieldComponent } from '../../../shared/payee-form-fields/payee-password-form-field/payee-password-form-field.component';
import { CustomCheckboxComponent } from '../../custom-checkbox/custom-checkbox.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-signup-finalization',
  templateUrl: './signup-finalization.component.html',
  styleUrls: ['./signup-finalization.component.scss'],
  imports: [
    HeaderAppComponent,
    PayeeIconComponent,
    FormTemplateComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatError,
    PayeePhoneComponent,
    PayeePasswordFormFieldComponent,
    CustomCheckboxComponent,
    PayeeButtonComponent,
    MatProgressSpinner,
    TranslateModule,
  ],
})
export class SignupFinalizationComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private clientsService = inject(ClientsService);
  private readonly token: string;

  readonly registerForm = this.fb.group({
    lastName: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    firstName: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    phoneNumber: this.fb.control('', {
      validators: Validators.required,
      nonNullable: true,
    }),
    password: this.fb.control('', {
      validators: [Validators.required, Validators.minLength(6)],
      nonNullable: true,
    }),
    newsletter: this.fb.control(false, { nonNullable: true }),
  });

  readonly loading = signal(false);
  readonly email: string;

  get lastName() {
    return this.registerForm.controls.lastName;
  }
  get firstName() {
    return this.registerForm.controls.firstName;
  }
  get phoneNumber() {
    return this.registerForm.controls.phoneNumber;
  }
  get password() {
    return this.registerForm.controls.password;
  }
  get newsletter() {
    return this.registerForm.controls.newsletter;
  }

  constructor() {
    this.token = this.route.snapshot.queryParams.token;
    this.email = this.route.snapshot.queryParams.email;

    if (!this.token || !this.email) {
      console.log('Token or email missing', {
        email: this.email,
        token: this.token,
      });
      this.router.navigateByUrl('/signup');
    }
  }

  async ngOnInit(): Promise<void> {
    await this.redirectIfAlreadyVerified();
  }

  async finalizeSignup() {
    if (this.registerForm.invalid || this.loading()) {
      Object.values(this.registerForm.controls).forEach((control) =>
        control.markAsDirty(),
      );

      return;
    }

    try {
      this.loading.set(true);
      const value = this.registerForm.getRawValue();
      const name = `${value.lastName} ${value.firstName}`;

      await this.authService.finalizeSignup(
        {
          name,
          password: value.password,
          is_newsletter_enabled: value.newsletter,
          token: this.token,
          phone: value.phoneNumber,
        },
        this.email,
      );

      await this.usersService.setUser();
      const client = await this.clientsService.getSelectedClient();
      if (
        !!client.draft_cases ||
        !!client.ongoing_cases ||
        !!client.closed_cases
      ) {
        await this.router.navigateByUrl(environment.afterLoginPath);
      } else {
        await this.router.navigateByUrl('case');
      }
      this.pushSignupEvent();
    } catch (error: AxiosError | unknown) {
      let message = this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR');

      if (error instanceof AxiosError) {
        if (error.response?.data?.code === 'already-verified') {
          await this.usersService.setUser();
          await this.clientsService.getSelectedClient();
          await this.router.navigateByUrl(environment.afterLoginPath);
          return;
        }
        if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        console.error(
          'Network error while verifying email',
          error.response?.data,
        );
      } else {
        console.error('Unknown error while verifying email', error);
      }

      this.snackbar.open(message, this.translate.instant('OK'), {
        duration: 15000,
      });
    } finally {
      this.loading.set(false);
    }
  }

  private async redirectIfAlreadyVerified(): Promise<void> {
    try {
      this.authService.logoutOnUnauthorized = false;
      const user = await this.usersService.setUser();
      if (user.email_verified) {
        await this.clientsService.getSelectedClient();
        await this.router.navigateByUrl(environment.afterLoginPath);
      }
    } catch {
      // User is not logged in yet — show the finalization form.
    } finally {
      this.authService.logoutOnUnauthorized = true;
    }
  }

  private pushSignupEvent(): void {
    window.dataLayer?.push({
      event: 'sign_up',
      method:
        this.usersService.userSubject.value?.origin === 'PLUGIN_SZAMLAZZHU'
          ? 'szamlazz'
          : 'email',
    });
  }
}
